import { useMemo } from 'react';
import { GroupBase } from 'react-select';

import { Option } from '@/react/components/form-components/PortainerSelect';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { HelmRelease } from '@/react/kubernetes/helm/types';

import {
  detectChartReference,
  ChartReferenceDetectionResult,
} from './detectChartReference';
import { useIsChartSourceValid } from './useIsChartSourceValid';
import { useIsChartValid } from './useIsChartValid';

export interface ChartReferenceState extends ChartReferenceDetectionResult {
  source: ReturnType<typeof useIsChartSourceValid>;
  chart: ReturnType<typeof useIsChartValid>;
}

/**
 * Combines static chart-reference detection with dynamic reachability checks.
 */
export function useChartReferenceState(
  release: HelmRelease,
  registryOptions: GroupBase<Option<RegistryValue>>[]
): ChartReferenceState {
  const detection = useMemo(
    () => detectChartReference({ release, registryOptions }),
    [release, registryOptions]
  );

  // Check source/chart validity and clear values if invalid
  const source = useIsChartSourceValid(
    release.chartReference,
    detection.selectedRegistry
  );
  const chart = useIsChartValid(release.chartReference);

  const selectedRegistry = source.hasError
    ? undefined
    : detection.selectedRegistry;
  const requiresSourceConfirmation = source.hasError
    ? false
    : detection.requiresSourceConfirmation;
  // the chart name also depends on the source, so clear it on a source error too
  const selectedChartName =
    source.hasError || chart.hasError ? undefined : detection.selectedChartName;
  const requiresChartConfirmation =
    source.hasError || chart.hasError
      ? false
      : detection.requiresChartConfirmation;

  const infoAlert = pickInfoAlert(source, chart, detection.infoAlert);

  return {
    ...detection,
    selectedRegistry,
    requiresSourceConfirmation,
    selectedChartName,
    requiresChartConfirmation,
    infoAlert,
    source,
    chart,
  };
}

function pickInfoAlert(
  source: ReturnType<typeof useIsChartSourceValid>,
  chart: ReturnType<typeof useIsChartValid>,
  detectionMsg?: string
) {
  if (detectionMsg) return detectionMsg;
  if (source.hasError) return source.errorMessage;
  if (chart.hasError) return chart.errorMessage;
  return undefined;
}
